import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import OnboardingQuiz, { shouldShowOnboarding } from '../components/OnboardingQuiz';
import SEO from '../components/SEO';
import EmailCapture from '../components/EmailCapture';
import { useIsMobile } from '../hooks/useWindowWidth';
import { isPro, isSubscribed, markSubscribed } from '../utils/featureGate';
import { BLOG_POSTS } from '../data/blogPosts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Use blog posts as the guide source — dedup by slug, filter out nulls
const _validPosts = (BLOG_POSTS || []).filter(p => p && p.slug && p.title);
const _seen = new Set();
const GUIDE_POOL = _validPosts.filter(p => { if (_seen.has(p.slug)) return false; _seen.add(p.slug); return true; });

// Rotates every 1 hour — pseudo-random per hour (consistent within the hour)
function getRotatingGuide() {
  const slot = Math.floor(Date.now() / (60 * 60 * 1000));
  const seed = (slot * 2654435761) >>> 0; // Knuth multiplicative hash
  return GUIDE_POOL[seed % GUIDE_POOL.length];
}

function GuidePill() {
  const [guide, setGuide] = useState(getRotatingGuide);

  useEffect(() => {
    // Check every minute; swap guide when the hour rolls over
    const interval = setInterval(() => {
      setGuide(getRotatingGuide());
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (!guide) return null;

  return (
    <div style={{ textAlign: 'center', margin: '0 0 28px' }}>
      <Link
        to={`/blog/${guide.slug}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px',
          background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.18)',
          borderRadius: '40px', padding: '10px 20px',
          color: '#94a3b8', fontSize: '13px', fontWeight: '600',
          textDecoration: 'none', transition: 'border-color 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(56,189,248,0.45)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(56,189,248,0.18)'}
      >
        <span style={{ fontSize: '15px' }}>📖</span>
        <span>Read: <span style={{ color: '#38bdf8', fontWeight: '700' }}>{guide.title}</span></span>
        <span style={{ color: '#475569', fontSize: '11px' }}>→</span>
      </Link>
    </div>
  );
}

// Mood → color mapping
const moodColors = {
  'Bullish': '#22c55e', 'Strongly Bullish': '#16a34a', 'Cautiously Bullish': '#4ade80',
  'Cautiously Bearish': '#f59e0b', 'Bearish': '#ef4444', 'Strongly Bearish': '#dc2626',
  'Neutral': '#38bdf8', 'Mixed': '#a78bfa', 'Volatile': '#fb923c',
};

function BriefHeroCard() {
  const [brief, setBrief] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState('');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const alreadySubscribed = isSubscribed();

  useEffect(() => {
    fetch(`${API}/api/brief?section=daily-premarket`)
      .then(r => r.json())
      .then(d => {
        const b = Array.isArray(d) ? d[0] : (d?.data || d);
        setBrief(b);
      }).catch(() => {});
  }, []);

  const moodColor = brief?.mood ? (moodColors[brief.mood] || '#38bdf8') : '#38bdf8';

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await fetch(`${API}/api/email-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source: 'brief-hero' }),
      });
      markSubscribed(email.trim());
      setEmailSent(true);
    } catch {}
  };

  return (
    <div
      onClick={() => navigate('/brief')}
      style={{
        background: 'linear-gradient(135deg, #0f1e3d 0%, #0a1628 60%, #0c1a2e 100%)',
        border: `1px solid ${moodColor}33`,
        borderRadius: '16px',
        padding: isMobile ? '20px 18px' : '28px 32px',
        margin: '24px 0 8px',
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'left',
        boxShadow: `0 0 40px ${moodColor}15`,
        cursor: 'pointer',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}>
      {/* Glow */}
      <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px', background: `radial-gradient(circle, ${moodColor}18 0%, transparent 70%)`, pointerEvents: 'none' }} />

      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <span style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' }}>
          📰 TODAY'S BRIEF
        </span>
        <span style={{ color: '#475569', fontSize: '12px' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
        </span>
        {brief?.mood && (
          <span style={{ background: `${moodColor}18`, border: `1px solid ${moodColor}40`, color: moodColor, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
            {brief.mood}
          </span>
        )}
      </div>

      {/* Hook or placeholder */}
      <p style={{ fontSize: isMobile ? '17px' : '20px', fontWeight: '700', color: '#f8fafc', lineHeight: 1.4, margin: '0 0 8px', maxWidth: '640px' }}>
        {brief?.hook || brief?.soWhat || 'Markets are moving. Here\'s what it means for your money.'}
      </p>
      <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 20px' }}>
        Plain English market intelligence — updated every morning
      </p>

      {/* Email capture — hide if already subscribed or logged in */}
      {!alreadySubscribed && !emailSent ? (
        <form onSubmit={handleEmailSignup} onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="your@email.com"
            style={{ flex: 1, minWidth: '200px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '10px 14px', color: '#f8fafc', fontSize: '14px', outline: 'none' }}
          />
          <button type="submit" style={{ background: `linear-gradient(135deg, ${moodColor}, #a78bfa)`, color: '#0f172a', border: 'none', borderRadius: '8px', padding: '10px 18px', fontWeight: '800', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Get Daily Brief Free →
          </button>
        </form>
      ) : emailSent ? (
        <div style={{ color: '#22c55e', fontWeight: '700', fontSize: '14px' }}>✅ You're in! Check your inbox tomorrow morning.</div>
      ) : null}

      <div onClick={e => e.stopPropagation()} style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Link to="/brief" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #38bdf8, #6366f1)', color: '#0f172a', fontWeight: '800', fontSize: '14px', padding: '10px 20px', borderRadius: '10px', textDecoration: 'none', boxShadow: '0 0 20px rgba(56,189,248,0.3)' }}>
          📰 Read Today's Brief →
        </Link>
        {!isPro() && (
          <Link to="/pricing" onClick={e => e.stopPropagation()} style={{ color: '#a78bfa', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}>
            ✨ Unlock Pro →
          </Link>
        )}
      </div>
    </div>
  );
}

// Mini explainers for landing page bubbles
const MINI_EXPLAIN = {
  BTC:      'Bitcoin is the world\'s first and largest cryptocurrency — digital money with a fixed supply of 21 million coins. High risk, high reward.',
  ETH:      'Ethereum is a programmable blockchain and the second-largest crypto. Powers decentralized apps and smart contracts.',
  'S&P 500':'500 largest US companies in one index. Historically ~10% average annual return. The most common starting point for investors.',
  DOW:      '30 large "blue chip" US companies like Apple and JPMorgan. Considered a barometer of the US economy.',
  NASDAQ:   'Tracks 3,000+ companies, heavily weighted toward tech. Higher growth potential, higher volatility than the S&P 500.',
  GOLD:     'Precious metal used as a store of value for thousands of years. Often rises during economic uncertainty or inflation.',
  OIL:      'Crude oil is the world\'s most traded commodity. Affects gas prices, shipping, and manufacturing costs globally.',
  '10Y':    'The 10-Year US Treasury yield. Directly influences mortgage rates — when this rises, home loans get more expensive.',
};

function MarketBubble({ item }) {
  const [open, setOpen] = useState(false);
  const pct = parseFloat(item.changePercent);
  const up = pct >= 0;
  const explain = MINI_EXPLAIN[item.symbol];

  return (
    <div
      onClick={() => explain && setOpen(o => !o)}
      style={{
        ...s.bubble,
        background: up ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
        borderColor: open ? 'rgba(56,189,248,0.5)' : up ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)',
        cursor: explain ? 'pointer' : 'default',
      }}
    >
      <div style={s.bubbleSym}>{item.symbol}</div>
      <div style={s.bubblePrice}>
        {item.price > 1000 ? '$' + item.price.toLocaleString('en-US', { maximumFractionDigits: 0 })
          : item.price > 1 ? '$' + item.price.toFixed(2) : '$' + (item.price?.toFixed(4) || '—')}
      </div>
      <div style={{ ...s.bubbleChange, color: up ? '#22c55e' : '#ef4444' }}>
        {item.changePercent != null ? `${up ? '+' : ''}${pct.toFixed(2)}%` : '—'}
      </div>
      {explain && !open && <div style={s.bubbleTap}>tap to explain</div>}
      {open && <div style={s.bubbleExplain}>{explain}</div>}
    </div>
  );
}

function makeStyles(isMobile = false) {
  return {
    page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' },
  
    // Hero
    hero: { position: 'relative', textAlign: 'center', padding: isMobile ? '80px 16px 60px' : '110px 40px 90px', overflow: 'hidden' },
    heroGlow: { position: 'absolute', top: '-120px', left: '50%', transform: 'translateX(-50%)', width: '900px', height: '700px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.18) 0%, rgba(56,189,248,0.05) 45%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
    heroGlow2: { position: 'absolute', top: '200px', left: '20%', width: '400px', height: '400px', background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.06) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
    heroInner: { position: 'relative', zIndex: 1 },
    eyebrow: { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '24px' },
    h1: { fontSize: 'clamp(42px, 7vw, 72px)', fontWeight: '900', lineHeight: 1.1, margin: '0 0 24px', letterSpacing: '-2px' },
    accent: { color: '#38bdf8' },
    sub: { fontSize: '18px', color: '#94a3b8', maxWidth: '520px', margin: '0 auto 40px', lineHeight: 1.75 },
    heroBtns: { display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' },
    btnPrimary: { background: '#38bdf8', color: '#0f172a', padding: '14px 32px', borderRadius: '10px', fontWeight: '700', fontSize: '16px', textDecoration: 'none', display: 'inline-block' },
    btnPrimaryAmber: { background: '#f59e0b', color: '#0f172a', padding: '14px 32px', borderRadius: '10px', fontWeight: '700', fontSize: '16px', textDecoration: 'none', display: 'inline-block' },
    btnGhost: { background: 'transparent', color: '#64748b', padding: '10px 24px', borderRadius: '10px', fontWeight: '600', fontSize: '14px', textDecoration: 'none', display: 'inline-block', border: '1px solid #334155' },
    btnSecondary: { background: 'transparent', color: '#38bdf8', padding: '14px 32px', borderRadius: '10px', fontWeight: '700', fontSize: '16px', textDecoration: 'none', border: '2px solid rgba(56,189,248,0.35)', display: 'inline-block' },
    btnOutline: { display: 'inline-block', background: 'transparent', color: '#64748b', border: '1px solid #334155', padding: '12px 28px', borderRadius: '10px', fontWeight: '600', fontSize: '15px', textDecoration: 'none' },
    trustRow: { display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' },
    trustBadge: { color: '#475569', fontSize: '13px' },
  
    // Choose Your Path
    pathSection: { padding: isMobile ? '0 16px 60px' : '0 40px 80px', background: '#0f172a' },
    pathInner: { maxWidth: '1100px', margin: '0 auto' },
    pathHeading: { textAlign: 'center', fontSize: 'clamp(26px,4vw,38px)', fontWeight: '800', margin: '0 0 10px', letterSpacing: '-0.5px' },
    pathSub: { textAlign: 'center', color: '#64748b', fontSize: '15px', marginBottom: '40px' },
    pathGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '20px' },
  
    pathCard: { background: '#1e293b', borderRadius: '20px', padding: isMobile ? '20px' : '32px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc', display: 'block', position: 'relative', transition: 'transform 0.15s, border-color 0.15s', minWidth: 0 },
    pathCardBlue: { border: '1px solid rgba(56,189,248,0.3)', background: 'linear-gradient(135deg, #1e293b 0%, rgba(56,189,248,0.06) 100%)' },
    pathCardPurple: { border: '1px solid rgba(167,139,250,0.25)', background: 'linear-gradient(135deg, #1e293b 0%, rgba(167,139,250,0.05) 100%)' },
    pathCardGreen: { border: '1px solid rgba(34,197,94,0.2)', background: 'linear-gradient(135deg, #1e293b 0%, rgba(34,197,94,0.04) 100%)' },
    pathCardBadge: { display: 'inline-block', background: 'rgba(56,189,248,0.12)', color: '#38bdf8', border: '1px solid rgba(56,189,248,0.25)', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', marginBottom: '16px', letterSpacing: '0.3px' },
    pathIcon: { fontSize: '40px', marginBottom: '14px', display: 'block' },
    pathTitle: { fontSize: '20px', fontWeight: '800', marginBottom: '10px', letterSpacing: '-0.3px', color: '#f1f5f9' },
    pathDesc: { color: '#64748b', fontSize: '14px', lineHeight: 1.65, marginBottom: '20px' },
    pathPreview: { background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '8px' },
    pathStep: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#94a3b8' },
    pathStepNum: { background: 'rgba(56,189,248,0.2)', color: '#38bdf8', width: '20px', height: '20px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800', flexShrink: 0 },
    pathCta: { color: '#38bdf8', fontWeight: '700', fontSize: '14px' },
  
    // Health Score
    scoreSection: { background: 'linear-gradient(135deg, rgba(56,189,248,0.04) 0%, rgba(99,102,241,0.04) 100%)', borderTop: '1px solid #1e293b', borderBottom: '1px solid #1e293b', padding: isMobile ? '48px 16px' : '80px 40px' },
    scoreInner: { maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '60px', alignItems: 'center' },
    scoreLeft: {},
    scoreEyebrow: { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' },
    scoreTitle: { fontSize: 'clamp(24px,3vw,36px)', fontWeight: '800', margin: '0 0 16px', letterSpacing: '-0.5px' },
    scoreSub: { color: '#64748b', fontSize: '15px', lineHeight: 1.7, margin: '0 0 28px' },
    scoreRight: {},
    scoreMock: { background: '#1e293b', borderRadius: '18px', padding: '28px', border: '1px solid rgba(56,189,248,0.2)' },
    scoreMockTitle: { fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
    scoreBig: { fontSize: '64px', fontWeight: '900', color: '#38bdf8', lineHeight: 1, marginBottom: '20px', letterSpacing: '-2px' },
    scoreOf: { fontSize: '24px', color: '#334155', fontWeight: '600' },
    scoreBars: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' },
    scoreBarRow: { display: 'flex', alignItems: 'center', gap: '10px' },
    scoreBarLabel: { fontSize: '12px', color: '#94a3b8', width: '110px', flexShrink: 0 },
    scoreBarTrack: { flex: 1, background: '#0f172a', borderRadius: '4px', height: '6px' },
    scoreBarFill: { height: '100%', borderRadius: '4px', transition: 'width 0.5s' },
    scoreBarPct: { fontSize: '12px', fontWeight: '700', width: '26px', textAlign: 'right', flexShrink: 0 },
    scoreMockNote: { fontSize: '11px', color: '#334155', textAlign: 'center' },
  
    // Tracker section
    trackerSection: { padding: isMobile ? '48px 16px' : '80px 40px', background: '#080e1a', borderTop: '1px solid #1e293b' },
    trackerInner: { maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '60px', alignItems: 'center' },
    trackerLeft: {},
    trackerRight: {},
    trackerEyebrow: { display: 'inline-block', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#f59e0b', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' },
    trackerTitle: { fontSize: 'clamp(22px,3vw,34px)', fontWeight: '900', margin: '0 0 16px', letterSpacing: '-0.5px', lineHeight: 1.2 },
    trackerSub: { color: '#64748b', fontSize: '15px', lineHeight: 1.7, margin: '0 0 20px' },
    trackerFeatures: { listStyle: 'none', padding: 0, margin: '0 0 28px' },
    trackerFeat: { color: '#94a3b8', fontSize: '14px', padding: '5px 0', lineHeight: 1.5 },
    proTag: { display: 'inline-block', background: 'rgba(56,189,248,0.12)', color: '#38bdf8', fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px', marginLeft: '6px', verticalAlign: 'middle' },
    trackerMock: { background: '#1e293b', borderRadius: '18px', padding: '24px', border: '1px solid rgba(245,158,11,0.2)' },
    trackerMockTitle: { fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '16px' },
    trackerStatRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100px, 100%), 1fr))', gap: '10px', marginBottom: '20px' },
    trackerStat: { background: '#0f172a', borderRadius: '10px', padding: '10px 8px', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' },
    trackerStatIcon: { fontSize: '18px', flexShrink: 0 },
    trackerStatVal: {},
    trackerMockBars: { background: '#0f172a', borderRadius: '10px', padding: '14px', marginBottom: '14px' },
    trackerAiTeaser: { background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: '10px', padding: '14px', marginBottom: '10px' },
    trackerAiHeader: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '10px' },
    trackerAiLine: { fontSize: '12px', color: '#64748b', padding: '4px 0', lineHeight: 1.5 },
    trackerMockNote: { fontSize: '11px', color: '#334155', textAlign: 'center', marginTop: '6px' },
  
    // Courses
    coursesSection: { padding: isMobile ? '48px 16px' : '80px 40px', background: '#080e1a' },
    coursesInner: { maxWidth: '1100px', margin: '0 auto' },
    coursesBadge: { display: 'inline-block', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', color: '#a5b4fc', padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', marginBottom: '16px' },
    coursesTitle: { fontSize: 'clamp(24px,3vw,36px)', fontWeight: '800', margin: '0 0 12px', letterSpacing: '-0.5px', textAlign: 'center' },
    coursesSub: { color: '#64748b', fontSize: '15px', textAlign: 'center', maxWidth: '500px', margin: '0 auto 36px', lineHeight: 1.6 },
    coursesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '12px' },
    courseCard: { display: 'flex', alignItems: 'center', gap: '16px', background: '#1e293b', borderRadius: '14px', padding: '18px 20px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc' },
    courseIcon: { fontSize: '28px', flexShrink: 0 },
    courseInfo: { flex: 1 },
    courseTitle: { fontSize: '15px', fontWeight: '700', marginBottom: '3px', color: '#f1f5f9' },
    courseDesc: { fontSize: '13px', color: '#64748b', lineHeight: 1.4 },
    courseArrow: { color: '#334155', fontSize: '16px', flexShrink: 0 },
  
    // Markets
    marketSection: { padding: isMobile ? '48px 16px' : '80px 40px', maxWidth: '1100px', margin: '0 auto' },
    marketHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', justifyContent: 'center' },
    sectionTitle: { textAlign: 'center', fontSize: 'clamp(24px,3vw,36px)', fontWeight: '800', margin: '0 0 12px', letterSpacing: '-0.5px' },
    sectionSub: { textAlign: 'center', color: '#64748b', fontSize: '15px', margin: '0 auto 36px', maxWidth: '520px', lineHeight: 1.6 },
    liveTag: { background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' },
    loadingRow: { textAlign: 'center', color: '#475569', padding: '20px 0' },
    bubblesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '16px' },
    bubble: { borderRadius: '14px', padding: '16px 12px', border: '1px solid', textAlign: 'center', transition: 'border-color 0.15s' },
    bubbleSym: { fontSize: '11px', fontWeight: '800', color: '#94a3b8', letterSpacing: '1px', marginBottom: '6px' },
    bubblePrice: { fontSize: '16px', fontWeight: '800', color: '#f8fafc', marginBottom: '4px' },
    bubbleChange: { fontSize: '13px', fontWeight: '700', marginBottom: '6px' },
    bubbleTap: { fontSize: '10px', color: '#334155', marginTop: '4px' },
    bubbleExplain: { marginTop: '8px', fontSize: '11px', color: '#94a3b8', lineHeight: 1.5, textAlign: 'left', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '8px' },
    linkBlue: { color: '#38bdf8', textDecoration: 'none', fontSize: '14px', fontWeight: '600' },
  
    // Tools strip
    toolsSection: { padding: isMobile ? '48px 16px' : '80px 40px', maxWidth: '1100px', margin: '0 auto' },
    toolsInner: {},
    toolsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: '14px', marginTop: '32px' },
    toolCard: { display: 'flex', alignItems: 'flex-start', gap: '16px', background: '#1e293b', borderRadius: '14px', padding: '20px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc' },
    toolIcon: { fontSize: '28px', flexShrink: 0 },
    toolTitle: { fontSize: '15px', fontWeight: '700', marginBottom: '4px', color: '#f1f5f9' },
    toolDesc: { fontSize: '13px', color: '#64748b', lineHeight: 1.4 },
  
    // Who it's for
    forSection: { background: '#080e1a', padding: isMobile ? '48px 16px' : '80px 40px' },
    forInner: { maxWidth: '960px', margin: '0 auto' },
    forGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px', marginTop: '36px' },
    forCard: { background: '#1e293b', borderRadius: '16px', padding: '28px', border: '1px solid #334155', textAlign: 'center' },
    forIcon: { fontSize: '36px', marginBottom: '12px' },
    forTitle: { fontSize: '17px', fontWeight: '700', marginBottom: '8px', margin: '0 0 8px' },
    forDesc: { color: '#64748b', fontSize: '14px', lineHeight: 1.6, margin: 0 },
  
    // Pricing
    pricingTeaser: { position: 'relative', padding: isMobile ? '48px 16px' : '80px 40px', overflow: 'hidden' },
    pricingGlow: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '700px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.07) 0%, transparent 60%)', pointerEvents: 'none' },
    pricingInner: { maxWidth: '700px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 },
    pricingCards: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '24px', textAlign: 'left' },
    priceFree: { background: '#1e293b', borderRadius: '16px', padding: '28px', border: '1px solid #334155' },
    pricePro: { background: 'linear-gradient(135deg, rgba(56,189,248,0.08) 0%, #1e293b 100%)', borderRadius: '16px', padding: '28px', border: '2px solid #38bdf8', position: 'relative' },
    proBadge: { background: '#38bdf8', color: '#0f172a', fontSize: '10px', fontWeight: '800', padding: '3px 10px', borderRadius: '20px', display: 'inline-block', marginBottom: '8px', letterSpacing: '0.5px', textTransform: 'uppercase' },
    planLabel: { fontSize: '16px', fontWeight: '700', marginBottom: '4px', color: '#f1f5f9' },
    planPrice: { fontSize: '40px', fontWeight: '900', lineHeight: 1, marginBottom: '8px', color: '#f8fafc' },
    mo: { fontSize: '18px', fontWeight: '500', color: '#64748b' },
    planDesc: { color: '#64748b', fontSize: '13px', lineHeight: 1.5, marginBottom: '16px' },
  
    // CTA
    ctaSection: { position: 'relative', textAlign: 'center', padding: isMobile ? '60px 16px' : '100px 40px', overflow: 'hidden' },
    ctaGlow: { position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '800px', height: '500px', background: 'radial-gradient(ellipse at center bottom, rgba(56,189,248,0.12) 0%, transparent 60%)', pointerEvents: 'none' },
    ctaH2: { position: 'relative', zIndex: 1, fontSize: 'clamp(28px,4vw,44px)', fontWeight: '800', marginBottom: '16px', letterSpacing: '-0.5px' },
    ctaSub: { position: 'relative', zIndex: 1, color: '#64748b', fontSize: '16px', marginBottom: '36px' },
    legal: { position: 'relative', zIndex: 1, color: '#334155', fontSize: '12px', marginTop: '20px' },
  
    // Social proof
    socialSection: { padding: isMobile ? '40px 16px 20px' : '60px 40px 20px', maxWidth: '1100px', margin: '0 auto' },
    socialInner: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '20px', marginBottom: '40px', textAlign: 'center' },
    socialStat: { background: '#1e293b', borderRadius: '14px', padding: '24px', border: '1px solid #334155' },
    socialStatNum: { fontSize: '40px', fontWeight: '900', color: '#38bdf8', marginBottom: '6px', letterSpacing: '-1px' },
    socialStatLabel: { color: '#64748b', fontSize: '13px', lineHeight: 1.5 },
    testimonialsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' },
    testimonialCard: { background: '#1e293b', borderRadius: '14px', padding: '24px', border: '1px solid #334155' },
    testimonialStars: { color: '#f59e0b', fontSize: '14px', marginBottom: '12px', letterSpacing: '2px' },
    testimonialText: { color: '#e2e8f0', fontSize: '15px', lineHeight: 1.65, fontStyle: 'italic', margin: '0 0 14px' },
    testimonialAuthor: { color: '#475569', fontSize: '13px', fontWeight: '600' },
  
    // Market Brief teaser
    briefTeaser: { background: '#080e1a', padding: isMobile ? '48px 16px' : '80px 40px' },
    briefTeaserInner: { maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '48px', alignItems: 'center' },
    briefTeaserLeft: {},
    briefTeaserEyebrow: { display: 'inline-block', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', marginBottom: '16px' },
    briefTeaserTitle: { fontSize: 'clamp(22px,3vw,32px)', fontWeight: '800', color: '#f1f5f9', margin: '0 0 14px', letterSpacing: '-0.5px', lineHeight: 1.25 },
    briefTeaserSub: { color: '#64748b', fontSize: '16px', lineHeight: 1.65, margin: '0 0 24px' },
    briefTeaserBtn: { display: 'inline-block', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', color: '#f59e0b', padding: '12px 24px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', textDecoration: 'none' },
    briefTeaserRight: {},
    briefMock: { background: '#1e293b', borderRadius: '14px', border: '1px solid #334155', padding: '20px', maxWidth: '360px' },
    briefMockHeader: { fontSize: '12px', fontWeight: '700', color: '#f59e0b', marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid #334155' },
    briefMockStory: { display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px' },
    briefMockNum: { background: 'rgba(56,189,248,0.1)', color: '#38bdf8', borderRadius: '4px', padding: '2px 7px', fontSize: '10px', fontWeight: '800', flexShrink: 0, marginTop: '2px' },
    briefMockHeadline: { fontSize: '13px', fontWeight: '700', color: '#f1f5f9', marginBottom: '4px' },
    briefMockTag: { fontSize: '11px', color: '#f59e0b' },
    briefMockSection: { background: 'rgba(56,189,248,0.05)', border: '1px solid rgba(56,189,248,0.1)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px' },
    briefMockSectionLabel: { fontSize: '10px', fontWeight: '700', color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' },
    briefMockSectionText: { fontSize: '12px', color: '#64748b', lineHeight: 1.55 },
  };
}

const s = makeStyles();

export default function Landing() {
  const isMobile = useIsMobile();
  const s = makeStyles(isMobile);

  const location = useLocation();
  const [proWelcome, setProWelcome] = useState(new URLSearchParams(location.search).get('welcome') === 'pro');
  const [marketData, setMarketData] = useState([]);
  const [marketLoading, setMarketLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [pulseHeadlines, setPulseHeadlines] = useState([]);

  const fetchPulse = useCallback(() => {
    // Try editorial articles first (KnowFi-branded), fall back to raw RSS
    fetch(`${API}/api/editorial`)
      .then(r => r.json())
      .then(d => {
        const articles = d.articles || [];
        if (articles.length >= 3) {
          setPulseHeadlines(articles.slice(0, 3).map(a => ({
            title: a.headline,
            link: '/market-pulse',
            source: 'KnowFi Editorial',
            pubDate: a.published_at,
          })));
        } else {
          // Fall back to raw news
          return fetch(`${API}/api/news`)
            .then(r => r.json())
            .then(d2 => setPulseHeadlines((d2.articles || []).slice(0, 3)));
        }
      })
      .catch(() => {
        fetch(`${API}/api/news`)
          .then(r => r.json())
          .then(d => setPulseHeadlines((d.articles || []).slice(0, 3)))
          .catch(() => {});
      });
  }, []);

  useEffect(() => {
    const fetch1 = () => {
      fetch(`${API}/api/market-data`)
        .then(r => r.json())
        .then(d => { setMarketData(d.data || []); setMarketLoading(false); })
        .catch(() => setMarketLoading(false));
    };
    fetch1();
    fetchPulse();
    const t = setInterval(fetch1, 60000);
    const t2 = setInterval(fetchPulse, 90000);
    // Show onboarding quiz to new visitors after a 1.2s delay
    const timer = setTimeout(() => {
      if (shouldShowOnboarding()) setShowOnboarding(true);
    }, 1200);
    return () => { clearInterval(t); clearInterval(t2); clearTimeout(timer); };
  }, [fetchPulse]);

  return (
    <div style={s.page}>
      {proWelcome && (
        <div style={{ background: 'linear-gradient(90deg,#0ea5e9,#6366f1)', color: '#fff', textAlign: 'center', padding: '0.85rem 1rem', fontWeight: 600, fontSize: '1rem', position: 'relative' }}>
          🎉 Welcome to KnowFi Pro! Full access is now unlocked.
          <button onClick={() => setProWelcome(false)} style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#fff', fontSize: '1.2rem', cursor: 'pointer' }}>×</button>
        </div>
      )}
      <SEO
        title="KnowFi — Finance Explained Without the BS"
        description="Track hedge fund portfolios, get an AI money coach, follow insider trades, and build your financial plan. Free financial tools. No jargon. Plain English."
        path="/"
        keywords="personal finance, financial literacy, money coach, hedge fund tracker, financial tools, budgeting, investing"
      />
      {showOnboarding && <OnboardingQuiz onClose={() => setShowOnboarding(false)} />}

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section style={s.hero}>
        <div style={s.heroGlow} />
        <div style={s.heroGlow2} />
        <div style={s.heroInner}>
          <div style={s.eyebrow}>Powered by AI · Built for Real People</div>
          <h1 style={s.h1}>
            Finance explained<br />
            <span style={s.accent}>without the BS.</span>
          </h1>
          <p style={s.sub}>
            Straight talk about markets, investing, debt, and crypto —
            no Wall Street suits, no condescending advice. Just clarity.
          </p>
          {/* Brief hero card — the #1 CTA */}
          <BriefHeroCard />

          {/* Quick nav tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', maxWidth: '420px', margin: '20px auto 12px', width: '100%' }}>
            {/* Crypto Corner — hero tile */}
            <Link to="/crypto-corner" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, rgba(99,102,241,0.22), rgba(99,102,241,0.08))', border: '1px solid rgba(99,102,241,0.5)', borderRadius: '16px', padding: '20px 12px', textDecoration: 'none', boxShadow: '0 0 24px rgba(99,102,241,0.18)' }}>
              <span style={{ fontSize: '28px' }}>₿</span>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#818cf8' }}>Crypto Corner</span>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>BTC · ETH · SOL · more</span>
            </Link>
            {/* Market Pulse — hero tile */}
            <Link to="/market-pulse" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, rgba(239,68,68,0.22), rgba(239,68,68,0.08))', border: '1px solid rgba(239,68,68,0.45)', borderRadius: '16px', padding: '20px 12px', textDecoration: 'none', boxShadow: '0 0 24px rgba(239,68,68,0.15)' }}>
              <span style={{ fontSize: '28px' }}>⚡</span>
              <span style={{ fontSize: '14px', fontWeight: '800', color: '#f87171' }}>Market Pulse</span>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '500' }}>S&amp;P · NASDAQ · more</span>
            </Link>
            {/* My Dashboard — secondary tile */}
            <Link to="/tracker" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: '12px', padding: '13px 12px', textDecoration: 'none' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>My Dashboard</span>
            </Link>
            {/* Smart Money — secondary tile */}
            <Link to="/smart-money" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: '12px', padding: '13px 12px', textDecoration: 'none' }}>
              <span style={{ fontSize: '18px' }}>🧠</span>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Smart Money</span>
            </Link>
          </div>
          <div style={s.trustRow}>
            <span style={s.trustBadge}>✅ Plain English</span>
            <span style={s.trustBadge}>✅ Stocks &amp; Crypto</span>
            <span style={s.trustBadge}>✅ Free to start</span>
          </div>
        </div>
      </section>

      {/* ── CHOOSE YOUR PATH (star features) ─────────────────────────── */}
      {/* ── GUIDE PILL ───────────────────────────────────────────────── */}
      <GuidePill />

      <section style={s.pathSection}>
        <div style={s.pathInner}>
          <h2 style={s.pathHeading}>What do you need right now?</h2>
          <p style={s.pathSub}>Pick where you want to start. Free to try — some features unlock with Pro.</p>
          <div style={s.pathGrid}>

            {/* KnowFi Brief — Top Reason to Sign Up */}
            <Link to="/brief" style={{ ...s.pathCard, border: '2px solid rgba(245,158,11,0.5)', background: 'linear-gradient(135deg, #1e293b 0%, rgba(245,158,11,0.08) 100%)', boxShadow: '0 0 32px rgba(245,158,11,0.08)' }}>
              <div style={{ ...s.pathCardBadge, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', borderColor: 'rgba(245,158,11,0.4)', fontWeight: '800' }}>⭐ Top Reason to Sign Up</div>
              <div style={s.pathIcon}>📰</div>
              <h3 style={s.pathTitle}>KnowFi Brief</h3>
              <p style={s.pathDesc}>Markets explained every day in plain English. What happened, why it matters, and what it means for your money. Delivered fresh every morning.</p>
              <div style={{ ...s.pathPreview, borderColor: 'rgba(245,158,11,0.15)' }}>
                <div style={{ ...s.pathStep, color: '#fcd34d' }}>📈 Daily market snapshot</div>
                <div style={{ ...s.pathStep, color: '#fcd34d' }}>💡 What it means for your money</div>
                <div style={{ ...s.pathStep, color: '#fcd34d' }}>📋 Weekly deep-dives &amp; monthly outlooks <span style={s.proTag}>Pro</span></div>
                <div style={{ ...s.pathStep, color: '#fcd34d' }}>📊 Monthly market debriefs <span style={s.proTag}>Pro</span></div>
              </div>
              <div style={{ ...s.pathCta, color: '#f59e0b', fontSize: '15px' }}>Read Today's Brief →</div>
            </Link>

            {/* Monthly Financial Tracker */}
            <Link to="/tracker" style={{ ...s.pathCard, ...s.pathCardBlue }}>
              <div style={s.pathCardBadge}>Track Your Progress</div>
              <div style={s.pathIcon}>📊</div>
              <h3 style={s.pathTitle}>Monthly Financial Tracker</h3>
              <p style={s.pathDesc}>Set goals once. Update in 60 seconds each month. Watch your savings grow and debt shrink, then let AI tell you what to do next.</p>
              <div style={s.pathPreview}>
                <div style={s.pathStep}><span style={s.pathStepNum}>📈</span> Savings growing month-over-month</div>
                <div style={s.pathStep}><span style={s.pathStepNum}>💳</span> Debt visibly shrinking</div>
                <div style={s.pathStep}><span style={s.pathStepNum}>🤖</span> AI action plan every month <span style={s.proTag}>Pro</span></div>
              </div>
              <div style={s.pathCta}>Start Tracking →</div>
            </Link>

            {/* Playbooks */}
            <Link to="/playbooks" style={{ ...s.pathCard, ...s.pathCardPurple }}>
              <div style={{ ...s.pathCardBadge, background: 'rgba(167,139,250,0.15)', color: '#a78bfa', borderColor: 'rgba(167,139,250,0.3)' }}>Situation-Specific</div>
              <div style={s.pathIcon}>📋</div>
              <h3 style={s.pathTitle}>I Have a Specific Situation</h3>
              <p style={s.pathDesc}>Pre-built roadmaps for real life moments like debt payoff, buying a first home, starting a new job, or catching up on retirement.</p>
              <div style={s.pathPreview}>
                <div style={{ ...s.pathStep, color: '#c4b5fd' }}>🔴 Credit card debt</div>
                <div style={{ ...s.pathStep, color: '#c4b5fd' }}>🏠 Buying my first home</div>
                <div style={{ ...s.pathStep, color: '#c4b5fd' }}>📈 Starting to invest</div>
              </div>
              <div style={{ ...s.pathCta, color: '#a78bfa' }}>Find My Playbook →</div>
            </Link>

            {/* How-To */}
            <Link to="/howto" style={{ ...s.pathCard, ...s.pathCardGreen }}>
              <div style={{ ...s.pathCardBadge, background: 'rgba(34,197,94,0.1)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.25)' }}>Step-by-Step</div>
              <div style={s.pathIcon}>🏦</div>
              <h3 style={s.pathTitle}>How Do I Open an Account?</h3>
              <p style={s.pathDesc}>Step-by-step guides for every account type. Most people don't know where to start, so we walk you through it from the very beginning.</p>
              <div style={s.pathPreview}>
                <div style={{ ...s.pathStep, color: '#86efac' }}>🏦 Roth IRA</div>
                <div style={{ ...s.pathStep, color: '#86efac' }}>💼 401(k) enrollment</div>
                <div style={{ ...s.pathStep, color: '#86efac' }}>📈 Brokerage account</div>
              </div>
              <div style={{ ...s.pathCta, color: '#22c55e' }}>See All Guides →</div>
            </Link>

            {/* Courses */}
            <Link to="/courses" style={{ ...s.pathCard, border: '1px solid rgba(251,191,36,0.3)', background: 'linear-gradient(135deg, #1e293b 0%, rgba(251,191,36,0.05) 100%)' }}>
              <div style={{ ...s.pathCardBadge, background: 'rgba(251,191,36,0.1)', color: '#fbbf24', borderColor: 'rgba(251,191,36,0.3)' }}>100% Free</div>
              <div style={s.pathIcon}>🎓</div>
              <h3 style={s.pathTitle}>Learn Finance From Scratch</h3>
              <p style={s.pathDesc}>Six free courses covering 401k, IRAs, stocks, crypto, budgeting, and the economy. Written in plain English with quizzes at the end of each lesson.</p>
              <div style={s.pathPreview}>
                <div style={{ ...s.pathStep, color: '#fde68a' }}>📘 401k & IRAs explained</div>
                <div style={{ ...s.pathStep, color: '#fde68a' }}>📈 How to read stocks</div>
                <div style={{ ...s.pathStep, color: '#fde68a' }}>🧠 Quiz yourself after each lesson</div>
              </div>
              <div style={{ ...s.pathCta, color: '#fbbf24' }}>Start Learning Free →</div>
            </Link>

            {/* Blog/Guides */}
            <Link to="/blog" style={{ ...s.pathCard, border: '1px solid rgba(148,163,184,0.2)', background: 'linear-gradient(135deg, #1e293b 0%, rgba(148,163,184,0.04) 100%)' }}>
              <div style={{ ...s.pathCardBadge, background: 'rgba(148,163,184,0.1)', color: '#94a3b8', borderColor: 'rgba(148,163,184,0.25)' }}>Free Articles</div>
              <div style={s.pathIcon}>📰</div>
              <h3 style={s.pathTitle}>Read & Learn At Your Own Pace</h3>
              <p style={s.pathDesc}>In-depth articles on the money questions everyone Googles but never gets a straight answer to. No sponsored content and no fluff.</p>
              <div style={s.pathPreview}>
                <div style={{ ...s.pathStep, color: '#cbd5e1' }}>💡 Roth IRA vs 401k — which first?</div>
                <div style={{ ...s.pathStep, color: '#cbd5e1' }}>💡 What to do with $1,000 saved</div>
                <div style={{ ...s.pathStep, color: '#cbd5e1' }}>💡 Why Americans can't retire</div>
              </div>
              <div style={{ ...s.pathCta, color: '#94a3b8' }}>Browse All Articles →</div>
            </Link>

            {/* Money Type Quiz */}
            <Link to="/quiz" style={{ ...s.pathCard, border: '1px solid rgba(167,139,250,0.35)', background: 'linear-gradient(135deg, #1e293b 0%, rgba(139,92,246,0.08) 100%)' }}>
              <div style={{ ...s.pathCardBadge, background: 'rgba(139,92,246,0.15)', color: '#a78bfa', borderColor: 'rgba(139,92,246,0.35)' }}>NEW · Free Quiz</div>
              <div style={s.pathIcon}>🧠</div>
              <h3 style={s.pathTitle}>What's Your Money Type?</h3>
              <p style={s.pathDesc}>Take the 2-minute quiz and get a personalized money action plan. Find out if you're an Optimizer, Builder, Eliminator, or Avoider.</p>
              <div style={s.pathPreview}>
                <div style={{ ...s.pathStep, color: '#c4b5fd' }}>🎯 Personalized action plan</div>
                <div style={{ ...s.pathStep, color: '#c4b5fd' }}>🔍 Discover your blind spots</div>
                <div style={{ ...s.pathStep, color: '#c4b5fd' }}>🚀 Tools matched to your type</div>
              </div>
              <div style={{ ...s.pathCta, color: '#a78bfa' }}>Take the Quiz →</div>
            </Link>

          </div>
        </div>
      </section>

      {/* ── EMAIL CAPTURE ─────────────────────────────────────────────── */}
      <EmailCapture
        variant="inline"
        source="landing-middle"
        title="Get the KnowFi Brief in your inbox — free"
        subtitle="Plain English market updates every morning."
      />

      {/* ── LEGAL ────────────────────────────────────────────────────── */}
      <p style={s.legal}>⚠️ KnowFi is financial education only — not investment advice.</p>

    </div>
  );
}

const courses = [];
const tools = [];
const audience = [];
